import {useCallback, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import {mcpLimitLogStateActions} from '../slices/mcp-limit-log-state.slice';

import {useEmailSearch} from './useEmailSearch';

import type {McpLimitType} from 'src/openapi/admin/models/McpLimitType';

type UseAuditLogFiltersProps = {
    accountId: number,
    filters: Record<string, unknown>,
};

export const useAuditLogFilters = ({accountId, filters}: UseAuditLogFiltersProps) => {
    const dispatch = useDispatch();
    const {handleEmailSearch, clearEmailSearch} = useEmailSearch({accountId});

    const handleAccountEmailChange = useCallback(
        (value: string) => {
            handleEmailSearch(value, filters);
        },
        [filters, handleEmailSearch],
    );

    const handleAccountEmailClear = useCallback(() => {
        clearEmailSearch(filters);
    }, [filters, clearEmailSearch]);

    const handleLimitTypesChange = useCallback(
        (values?: McpLimitType[]) => {
            const {limitTypes: _, ...rest} = filters;
            dispatch(
                mcpLimitLogStateActions.setFilters({
                    ...rest,
                    ...(values?.length ? {limitTypes: values} : {}),
                }),
            );
        },
        [dispatch, filters],
    );

    const handleDateFilterChange = useCallback(
        (startDate: string | undefined, endDate: string | undefined) => {
            const {startDate: _, endDate: __, ...rest} = filters;
            const newFilters: Record<string, unknown> = {};
            if (startDate) {
                newFilters.startDate = startDate;
            }
            if (endDate) {
                newFilters.endDate = endDate;
            }
            dispatch(
                mcpLimitLogStateActions.setFilters({
                    ...rest,
                    ...newFilters,
                }),
            );
        },
        [dispatch, filters],
    );

    const clearAllFilters = useCallback(() => {
        dispatch(mcpLimitLogStateActions.setFilters({}));
    }, [dispatch]);

    const filtersCount = useMemo(() => {
        let count = 0;
        if (filters.accountEmail) {
            count++;
        }
        if ((filters.limitTypes as McpLimitType[] | undefined)?.length) {
            count++;
        }
        if (filters.startDate || filters.endDate) {
            count++;
        }
        return count;
    }, [filters]);

    return {
        handleAccountEmailChange,
        handleAccountEmailClear,
        handleLimitTypesChange,
        handleDateFilterChange,
        clearAllFilters,
        filtersCount,
    };
};
